// VirtualDesk backend REST client. Every request hits VITE_BACKEND_URL.
// Envelope: `{ data: ... }` on success, `{ error: { code, message } }` on failure.
// See docs/onboarding.md "Backend endpoints summary" for the contract.

import { getClientFingerprint, getSessionId, getVisitorId, setSessionId } from "./session";

export class ApiError extends Error {
  code: string;
  status: number;
  constructor(code: string, message: string, status: number) {
    super(message);
    this.code = code;
    this.status = status;
    this.name = "ApiError";
  }
}

export const BASE_URL = (import.meta.env.VITE_BACKEND_URL as string | undefined) ?? "http://localhost:8080";

export interface ApiEnvelope<T> {
  data?: T;
  error?: { code: string; message: string };
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
  token?: string,
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body && !(init.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (token) headers.set("Authorization", `Bearer ${token}`);

  // End-to-end session tracing — replay any session id we already have, plus
  // a stable per-tab fingerprint. Backend echoes back X-Session-Id, which we
  // capture below.
  const existingSession = getSessionId();
  if (existingSession) headers.set("X-Session-Id", existingSession);
  headers.set("X-Client-Fingerprint", getClientFingerprint());
  // Visitor id — long-lived (90-day) so the onboarding tracer can link a
  // visitor's pre-register journey to their eventual onboarding row even if
  // they bounce mid-flow and come back days later. The cookie is set by the
  // backend; we forward it explicitly so cross-origin requests (where the
  // cookie may not auto-propagate to *.run.app) still carry it.
  const visitorID = getVisitorId();
  if (visitorID) headers.set("X-Visitor-Id", visitorID);

  let res: Response;
  try {
    res = await fetch(`${BASE_URL}${path}`, { ...init, headers });
  } catch (err) {
    throw new ApiError("network_error", (err as Error).message, 0);
  }

  const newSession = res.headers.get("X-Session-Id");
  if (newSession && newSession !== existingSession) {
    setSessionId(newSession);
  }

  // 501 is a legal soft-error for "coming soon" endpoints — callers handle it.
  if (res.status === 501) {
    throw new ApiError("not_implemented", "Feature coming soon", 501);
  }

  const text = await res.text();
  let parsed: ApiEnvelope<T> | null = null;
  if (text) {
    try {
      parsed = JSON.parse(text) as ApiEnvelope<T>;
    } catch {
      throw new ApiError("invalid_response", "Backend returned non-JSON response", res.status);
    }
  }

  if (!res.ok) {
    const code = parsed?.error?.code ?? "unknown_error";
    const message = parsed?.error?.message ?? `Request failed (${res.status})`;
    throw new ApiError(code, message, res.status);
  }

  return (parsed?.data as T) ?? (undefined as unknown as T);
}

// ============================================================================
// Onboarding API (public — no auth required)
// See docs/onboarding.md for the 4-step flow:
//   1. register -> verify email
//   2. domain branch (teammate-request OR company form)
//   3. pick subdomain
//   4. hire first agent
// ============================================================================

export interface RegisterPayload {
  email: string;
  password: string;
}
export interface RegisterResponse {
  onboarding_id: string;
  email: string;
  // True only when a live provider (Resend) accepted the verification email.
  // The frontend MUST gate "Check your inbox" UI on this — never claim the
  // email was sent when it wasn't.
  verification_email_sent: boolean;
  // Provider that handled (or was supposed to handle) the email — "resend",
  // "noop", or "unconfigured". Useful for the dev / prod misconfiguration
  // banner.
  verification_email_provider: string;
  // Only present in non-prod environments when the noop sender is in play.
  // The wizard surfaces this as a "dev mode" link so the operator can
  // continue without an email vendor.
  dev_verify_url?: string;
}

// Resend response — same shape as Register's email fields plus the dev URL
// when the noop sender is active.
export interface ResendVerificationResponse {
  email_sent: boolean;
  email_provider: string;
  dev_verify_url?: string;
}

export interface VerifyEmailResponse {
  onboarding_id: string;
  email: string;
  domain: string;
  domain_exists: boolean;
  existing_client_id?: string;
}

export interface RequestTeammateAccessResponse {
  notified: true;
}

export interface CompanyFormPayload {
  first_name: string;
  last_name: string;
  business_name: string;
  // Structured address. address_line2 is optional; the rest are required by
  // both client and server validation. country_code is the 2-letter ISO
  // 3166-1 alpha-2 code (e.g. "US"), uppercased server-side before insert.
  address_line1: string;
  address_line2?: string;
  city: string;
  region: string;
  postal_code: string;
  country_code: string;
  business_website: string;
  business_phone: string;
  owner_phone: string;
}
export interface CompanyFormResponse {
  client_id: string;
  client_code: string;
}

export interface SubdomainCheckResponse {
  available: boolean;
  reason?: "taken" | "reserved" | "invalid_format";
}

export interface SubdomainSuggestion {
  subdomain: string;
  available: boolean;
}
export interface SubdomainSuggestionsResponse {
  suggestions: SubdomainSuggestion[];
}

export interface SetSubdomainResponse {
  client_id: string;
  client_code: string;
  subdomain: string;
  stage: OnboardingStageWire;
  dns_provisioned: boolean;
}

// Onboarding stages on the wire — mirrors backend's OnboardingStage enum.
export type OnboardingStageWire =
  | "registered"
  | "email_verified"
  | "company_profile"
  | "subdomain_selected"
  | "dns_pending"
  | "dns_verified"
  | "subdomain_ready"
  | "owner_invited_team"
  | "first_agent_hired"
  | "completed"
  | "abandoned";

export interface OnboardingMeResponse {
  onboarding_id: string;
  email: string;
  stage: OnboardingStageWire;
  email_verified: boolean;
  domain: string;
  client_id?: string;
  client_code?: string;
  company_name?: string;
  subdomain?: string;
  subdomain_ready_at?: string;
}

export interface HireFirstAgentResponse {
  agent_id: string;
}

// DNS status — surfaces both live booleans and the provisioning-timeline
// timestamps so the wizard's terminal page can render a checked timeline.
// dig-style live DNS lookup, refreshed on every /dns-status poll. `cname` is
// the unqualified target (no trailing dot); `addresses` is the resolved A/AAAA
// list. `error` / `cname_error` / `addresses_error` are populated only when
// the corresponding lookup fails — surfacing NXDOMAIN/SERVFAIL verbatim so the
// user can tell "not yet propagated" from "lookup blew up."
export interface DnsLookupResult {
  hostname: string;
  queried_at: string;
  resolver: string;
  cname: string;
  addresses: string[] | null;
  error?: string;
  cname_error?: string;
  addresses_error?: string;
}

export interface DnsStatusResponse {
  stage: OnboardingStageWire;
  subdomain: string;
  hostname?: string;
  record_id?: string;
  // Live booleans (snapshot at the time of the request).
  dns_record_created: boolean;
  dns_propagated: boolean;
  https_reachable: boolean;
  dns_verified: boolean;
  ssl_verified: boolean;
  // Provisioning-timeline timestamps — null until each milestone is reached.
  cname_created_at: string | null;
  domain_alias_claimed_at: string | null;
  cname_resolved_at: string | null;
  https_reachable_at: string | null;
  welcome_email_sent_at: string | null;
  subdomain_ready_at: string | null;
  // Live dig snapshot for the wizard's terminal page.
  dns_lookup?: DnsLookupResult;
}

export interface ProvisionDnsResponse {
  netlify_dns_record_id?: string;
  manual_instructions?: {
    type: string;
    hostname: string;
    value: string;
    ttl: number;
  };
}

export const onboardingApi = {
  register: (email: string, password: string) =>
    apiFetch<RegisterResponse>("/api/v1/public/onboarding/register", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  verifyEmail: (token: string) =>
    apiFetch<VerifyEmailResponse>(
      `/api/v1/public/onboarding/verify-email`,
      { method: "POST", body: JSON.stringify({ token }) },
    ),

  resendVerification: (onboardingId: string) =>
    apiFetch<ResendVerificationResponse>(
      `/api/v1/public/onboarding/verify-email/resend`,
      { method: "POST", body: JSON.stringify({ onboarding_id: onboardingId }) },
    ),

  // Authenticated onboarding state — called by the wizard right after sign-in
  // to resume from whichever stage the user left off at, even cross-device.
  me: (token: string) =>
    apiFetch<OnboardingMeResponse>(
      `/api/v1/public/onboarding/me`,
      {},
      token,
    ),

  requestTeammateAccess: (onboardingId: string, token: string) =>
    apiFetch<RequestTeammateAccessResponse>(
      `/api/v1/public/onboarding/${encodeURIComponent(onboardingId)}/request-teammate-access`,
      { method: "POST" },
      token,
    ),

  submitCompany: (onboardingId: string, body: CompanyFormPayload, token: string) =>
    apiFetch<CompanyFormResponse>(
      `/api/v1/public/onboarding/${encodeURIComponent(onboardingId)}/company`,
      { method: "POST", body: JSON.stringify(body) },
      token,
    ),

  checkSubdomain: (value: string) =>
    apiFetch<SubdomainCheckResponse>(
      `/api/v1/public/onboarding/subdomain/check?subdomain=${encodeURIComponent(value)}`,
    ),

  getSubdomainSuggestions: (onboardingId: string, token: string) =>
    apiFetch<SubdomainSuggestionsResponse>(
      `/api/v1/public/onboarding/${encodeURIComponent(onboardingId)}/subdomain/suggestions`,
      {},
      token,
    ),

  setSubdomain: (onboardingId: string, subdomain: string, token: string) =>
    apiFetch<SetSubdomainResponse>(
      `/api/v1/public/onboarding/${encodeURIComponent(onboardingId)}/subdomain`,
      { method: "POST", body: JSON.stringify({ subdomain }) },
      token,
    ),

  // Legacy — first-agent + complete have moved out of the wizard. Kept here
  // so any external integrations keep working.
  hireFirstAgent: (onboardingId: string, agentCode: string, token: string) =>
    apiFetch<HireFirstAgentResponse>(
      `/api/v1/public/onboarding/${encodeURIComponent(onboardingId)}/first-agent`,
      { method: "POST", body: JSON.stringify({ agent_code: agentCode }) },
      token,
    ),

  complete: (onboardingId: string, token: string) =>
    apiFetch<{ completed: true }>(
      `/api/v1/public/onboarding/${encodeURIComponent(onboardingId)}/complete`,
      { method: "POST" },
      token,
    ),

  provisionDns: (onboardingId: string, token: string) =>
    apiFetch<ProvisionDnsResponse>(
      `/api/v1/public/onboarding/${encodeURIComponent(onboardingId)}/provision-dns`,
      { method: "POST" },
      token,
    ),

  getDnsStatus: (onboardingId: string, token: string) =>
    apiFetch<DnsStatusResponse>(
      `/api/v1/public/onboarding/${encodeURIComponent(onboardingId)}/dns-status`,
      {},
      token,
    ),
};

// ============================================================================
// Public tenant lookup (unauthenticated)
// Used by the tenant login page to lock the @<domain> suffix on the email
// field — the subdomain is already public, so the owner email domain rides
// at the same exposure level.
// ============================================================================

export interface TenantEmailDomainResponse {
  subdomain: string;
  email_domain: string;
}

export const tenantsApi = {
  emailDomain: (subdomain: string) =>
    apiFetch<TenantEmailDomainResponse>(
      `/api/v1/public/tenants/${encodeURIComponent(subdomain)}/email-domain`,
    ),
};

// ============================================================================
// Tickets API (tenant-scoped)
// ============================================================================

// Status mirrors the Postgres ticket_status enum. Don't add "open" here —
// the backend doesn't recognize it, and a `?status=open` query results in a
// `pq: invalid input value for enum ticket_status` error. Aligned with
// backend models.TicketStatus + the OpenAPI spec.
export type TicketStatus =
  | "reported"
  | "queued"
  | "workinprogress"
  | "pending_approval"
  | "resolved"
  | "notabug"
  | "closed";
// Priority mirrors the Postgres ticket_priority enum + the backend's
// validation set. P1 (urgent) → P4 (low). Don't surface "low/medium/high"
// labels to the wire — those are presentation-only.
export type TicketPriority = "P1" | "P2" | "P3" | "P4";

export interface Ticket {
  id: string;
  external_id: string;
  subject: string;
  description: string;
  status: TicketStatus;
  priority: TicketPriority;
  // Wire field is `reporter_email` (matches backend models.Ticket json tag).
  reporter_email?: string;
  reporter_name?: string;
  assignee_email?: string;
  created_at: string;
  updated_at: string;
}

export interface TicketNote {
  id: string;
  ticket_id: string;
  author_email?: string;
  body: string;
  created_at: string;
  kind: "note" | "history";
}

export interface TicketDetail extends Ticket {
  notes: TicketNote[];
}

export interface CreateTicketPayload {
  subject: string;
  description: string;
  priority: TicketPriority;
  // Backend reads `customer_email` (matches the public ingest API + the
  // tickets.reporter_email column). The "requester" wording was an early
  // UI-side label that drifted from the wire contract.
  customer_email?: string;
}

export interface UpdateTicketPayload {
  status?: TicketStatus;
  priority?: TicketPriority;
  assignee_email?: string;
}

// ============================================================================
// Agents API (tenant-scoped)
// ============================================================================

export interface Agent {
  id: string;
  client_id: string;
  role_id: string;
  name: string;
  status: "active" | "paused" | "archived";
  role_code: string;
  role_display_name: string;
  role_category: string;
  created_at: string;
}

export interface ListAgentsResponse {
  agents: Agent[];
}

export interface HireAgentResponse {
  agent: {
    id: string;
    client_id: string;
    role_id: string;
    name: string;
    status: string;
    created_at: string;
  };
  role_code: string;
  role_display_name: string;
  role_category: string;
}

export interface AgentRoleFromBackend {
  id: string;
  code: string;
  display_name: string;
  category: string;
  description: string;
  runtime: string;
  price_tier?: string;
  is_active: boolean;
}

export const agentsApi = {
  list: (clientCode: string, token: string) =>
    apiFetch<ListAgentsResponse>(
      `/api/v1/tenants/${encodeURIComponent(clientCode)}/agents`,
      {},
      token,
    ),

  hire: (clientCode: string, roleCode: string, name: string | undefined, token: string) =>
    apiFetch<HireAgentResponse>(
      `/api/v1/tenants/${encodeURIComponent(clientCode)}/agents`,
      {
        method: "POST",
        body: JSON.stringify({ role_code: roleCode, name: name ?? "" }),
      },
      token,
    ),

  listRoles: (tenantCode: string, token: string) =>
    apiFetch<{ roles: AgentRoleFromBackend[] }>(
      `/api/v1/tenants/${encodeURIComponent(tenantCode)}/agent-roles`,
      {},
      token,
    ),
};

// ============================================================================
// Tenant auth — cross-tenant login guard
//
// After a successful Firebase sign-in on `<sub>.virtualdesk.ai/login`, the
// frontend calls validate-membership BEFORE loading any data. If the
// authenticated user is not a member of `<sub>`, the backend returns 404
// `account_not_found` (deliberately the same shape as "user does not exist"
// to avoid leaking which tenants a user belongs to) and writes a row to
// security_events / fires the SECURITY_WEBHOOK_URL fan-out. The frontend
// then signs the user out of Firebase and shows the same generic error.
// ============================================================================

export interface ValidateMembershipResponse {
  client_id: string;
  client_code: string;
  company_name: string;
  user_id: string;
  email: string;
  role: string;
}

export const tenantAuthApi = {
  validateMembership: (clientCode: string, token: string) =>
    apiFetch<ValidateMembershipResponse>(
      `/api/v1/tenants/${encodeURIComponent(clientCode)}/auth/validate-membership`,
      { method: "POST" },
      token,
    ),
};

// ============================================================================
// API Keys API (tenant-scoped)
// ============================================================================

// ApiKeyScope is intentionally a `string` and not a closed enum — the
// authoritative list now lives in the backend's apiscopes.Catalog and is
// fetched from `/api-keys/catalog`. The frontend renders whatever the
// catalog returns so adding a new role + scopes on the server doesn't need
// a coordinated frontend deploy.
export type ApiKeyScope = string;

export interface ApiKey {
  id: string;
  client_id: string;
  label: string;
  key_prefix: string;
  scopes: ApiKeyScope[];
  role_code: string;
  created_by: string;
  last_used_at?: string | null;
  expires_at?: string | null;
  revoked_at?: string | null;
  created_at: string;
}

export interface ListApiKeysResponse {
  api_keys: ApiKey[];
}

export interface MintApiKeyResponse {
  api_key: ApiKey;
  // The raw token. Only returned once on mint — never store this anywhere.
  secret: string;
}

// ApiKeyRoleEntry is one row from GET /api-keys/catalog — the set of scopes
// a role grants. `role_code` matches the agent_roles seeds (e.g.
// "cs.ticket_responder"). Scopes are display-ordered.
export interface ApiKeyRoleEntry {
  role_code: string;
  scopes: ApiKeyScope[];
}

export interface ApiKeyCatalogResponse {
  roles: ApiKeyRoleEntry[];
}

export const apiKeysApi = {
  list: (clientCode: string, token: string) =>
    apiFetch<ListApiKeysResponse>(
      `/api/v1/tenants/${encodeURIComponent(clientCode)}/api-keys`,
      {},
      token,
    ),

  mint: (
    clientCode: string,
    payload: { label: string; role_code: string; scopes: ApiKeyScope[] },
    token: string,
  ) =>
    apiFetch<MintApiKeyResponse>(
      `/api/v1/tenants/${encodeURIComponent(clientCode)}/api-keys`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
      token,
    ),

  catalog: (clientCode: string, token: string) =>
    apiFetch<ApiKeyCatalogResponse>(
      `/api/v1/tenants/${encodeURIComponent(clientCode)}/api-keys/catalog`,
      {},
      token,
    ),

  revoke: (clientCode: string, keyId: string, token: string) =>
    apiFetch<{ revoked: true }>(
      `/api/v1/tenants/${encodeURIComponent(clientCode)}/api-keys/${encodeURIComponent(keyId)}`,
      { method: "DELETE" },
      token,
    ),

  // Rotate revokes the existing key and mints a fresh one carrying the
  // same role + scopes. Returns the new APIKey row + raw secret + the id
  // of the row that was rotated out.
  rotate: (clientCode: string, keyId: string, token: string) =>
    apiFetch<MintApiKeyResponse & { replaced_id: string }>(
      `/api/v1/tenants/${encodeURIComponent(clientCode)}/api-keys/${encodeURIComponent(keyId)}/rotate`,
      { method: "POST" },
      token,
    ),

  // History returns revoked keys (most recently revoked first) so the UI
  // can render "Live from … to …" rows next to the active set.
  history: (clientCode: string, token: string) =>
    apiFetch<ListApiKeysResponse>(
      `/api/v1/tenants/${encodeURIComponent(clientCode)}/api-keys/history`,
      {},
      token,
    ),
};

// ============================================================================
// Integrations API (per-tenant Config tab)
// ============================================================================

// ResendIntegrationStatus reflects the Resend connection state. `connected`
// is false when the tenant hasn't minted a key yet (the API returns 200 with
// a thin payload in that case).
export interface ResendIntegrationStatus {
  connected: boolean;
  resend_key_id?: string;
  key_prefix?: string;
  name?: string;
  send_via_resend: boolean;
  connected_at?: string;
}

// ResendSetupStep mirrors backend integrations/resend.Step. Status values:
//   pending      — not started yet
//   in_progress  — running (or DNS still propagating, in the verify step)
//   done         — succeeded
//   failed       — last attempt errored; `detail` carries the reason
export interface ResendSetupStep {
  name: "domain_registered" | "dns_records_published" | "verified";
  status: "pending" | "in_progress" | "done" | "failed";
  detail?: string;
  started_at?: string;
  finished_at?: string;
}

export interface ResendSetupRecord {
  type: string;
  name: string;
  value: string;
  ttl?: number;
}

// ResendSetupState is the wizard snapshot returned by both POST
// /integrations/resend (when state isn't verified yet) and GET
// /integrations/resend/setup. overall_status mirrors Resend's raw value:
//   "pending" | "not_started" | "verified" | "failed" | "temporary_failure"
export interface ResendSetupState {
  hostname: string;
  resend_domain_id?: string;
  overall_status: string;
  steps: ResendSetupStep[];
  records?: ResendSetupRecord[];
}

// CreateResendResponse is the union the POST endpoint returns. When the
// domain isn't verified yet, the server replies with HTTP 202 and only
// `setup` + `awaiting_verify=true`. Once verified, it mints the key and
// returns 201 with `integration` populated.
export interface CreateResendResponse {
  integration?: ResendIntegrationStatus;
  setup?: ResendSetupState;
  connected?: boolean;
  awaiting_verify?: boolean;
}

export const integrationsApi = {
  getResend: (clientCode: string, token: string) =>
    apiFetch<{ integration: ResendIntegrationStatus }>(
      `/api/v1/tenants/${encodeURIComponent(clientCode)}/integrations/resend`,
      {},
      token,
    ),
  createResend: (
    clientCode: string,
    payload: { name?: string; send_via_resend?: boolean },
    token: string,
  ) =>
    apiFetch<CreateResendResponse>(
      `/api/v1/tenants/${encodeURIComponent(clientCode)}/integrations/resend`,
      { method: "POST", body: JSON.stringify(payload) },
      token,
    ),
  getResendSetup: (clientCode: string, token: string) =>
    apiFetch<{ setup: ResendSetupState }>(
      `/api/v1/tenants/${encodeURIComponent(clientCode)}/integrations/resend/setup`,
      {},
      token,
    ),
  patchResend: (
    clientCode: string,
    payload: { send_via_resend?: boolean },
    token: string,
  ) =>
    apiFetch<{ integration: ResendIntegrationStatus }>(
      `/api/v1/tenants/${encodeURIComponent(clientCode)}/integrations/resend`,
      { method: "PATCH", body: JSON.stringify(payload) },
      token,
    ),
  deleteResend: (clientCode: string, token: string) =>
    apiFetch<{ disconnected: true }>(
      `/api/v1/tenants/${encodeURIComponent(clientCode)}/integrations/resend`,
      { method: "DELETE" },
      token,
    ),
};

export const ticketsApi = {
  list: (clientCode: string, token: string, status?: TicketStatus) => {
    const qs = status ? `?status=${encodeURIComponent(status)}` : "";
    return apiFetch<Ticket[]>(
      `/api/v1/tenants/${encodeURIComponent(clientCode)}/tickets${qs}`,
      {},
      token,
    );
  },

  create: (clientCode: string, body: CreateTicketPayload, token: string) =>
    apiFetch<Ticket>(
      `/api/v1/tenants/${encodeURIComponent(clientCode)}/tickets`,
      { method: "POST", body: JSON.stringify(body) },
      token,
    ),

  get: (clientCode: string, ticketId: string, token: string) =>
    apiFetch<TicketDetail>(
      `/api/v1/tenants/${encodeURIComponent(clientCode)}/tickets/${encodeURIComponent(ticketId)}`,
      {},
      token,
    ),

  update: (clientCode: string, ticketId: string, body: UpdateTicketPayload, token: string) =>
    apiFetch<Ticket>(
      `/api/v1/tenants/${encodeURIComponent(clientCode)}/tickets/${encodeURIComponent(ticketId)}`,
      { method: "PATCH", body: JSON.stringify(body) },
      token,
    ),

  addNote: (clientCode: string, ticketId: string, body: string, token: string) =>
    apiFetch<TicketNote>(
      `/api/v1/tenants/${encodeURIComponent(clientCode)}/tickets/${encodeURIComponent(ticketId)}/notes`,
      { method: "POST", body: JSON.stringify({ body }) },
      token,
    ),
};
